import { Star } from "lucide-react";
import { toast } from "sonner";
import { addFavorite, type FavoriteKind, removeFavorite, useIsFavorited } from "@/lib/favorites";
import { cn } from "@/lib/utils";

const KIND_LABEL: Record<FavoriteKind, string> = {
  policy: "Policy",
  claim: "Claim",
  agency: "Agency",
};

interface FavoriteToggleProps {
  kind: FavoriteKind;
  // Stable identifier — policy number, claim number, or agent number. The
  // entity's URL is derived from kind + number on the home-page Favorites panel.
  number: string | null | undefined;
  // Display name (insured / claimant / agency). Stored with the favorite so
  // the home-page list doesn't have to refetch each entity just to label it.
  name: string | null | undefined;
  // "section-bar" tunes the colors for placement on a SectionBar's dark
  // primary background; "default" works on light card surfaces.
  variant?: "section-bar" | "default";
  className?: string;
}

// Star button used to pin / unpin an entity. Tuned by default for the dark
// header of a SectionBar — outline white when not pinned, filled amber when
// pinned. Click toggles; the favorites store is per-device localStorage so
// it persists across page reloads.
export function FavoriteToggle({
  kind,
  number,
  name,
  variant = "section-bar",
  className,
}: FavoriteToggleProps) {
  const isPinned = useIsFavorited(kind, number);
  if (!number) return null;

  function toggle() {
    if (!number) return;
    // Toast wording mirrors the action and labels the kind so a row of
    // identical-looking ★ buttons on the same dashboard (Policy + Agency)
    // doesn't leave the user guessing which one they just toggled.
    const label = `${KIND_LABEL[kind]} ${number}`;
    if (isPinned) {
      removeFavorite(kind, number);
      toast.success(`Removed ${label} from favorites`);
    } else {
      addFavorite({ kind, number, name: name ?? null });
      toast.success(`Added ${label} to favorites`);
    }
  }

  const isSectionBar = variant === "section-bar";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={isPinned}
      aria-label={isPinned ? "Remove from favorites" : "Add to favorites"}
      title={isPinned ? "Remove from favorites" : "Add to favorites"}
      className={cn(
        "inline-flex size-7 items-center justify-center rounded-md transition-colors",
        isSectionBar
          ? isPinned
            ? "text-amber-300 hover:bg-primary-foreground/10"
            : "text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          : isPinned
            ? "text-amber-500 hover:bg-accent hover:text-amber-600"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
    >
      <Star className={cn("size-4", isPinned && "fill-current")} aria-hidden="true" />
    </button>
  );
}
